import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './EmployeeJoiningForm.css';
import RegistrationNavbar from '../EmployeesProfile/PendingProfiles/EmployeeCreation/EmployeesRegistrationNavbar/EmployeeRegistartion/RegistrationNavbar';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getAuditLog } from '../../utils/calculateDaysInMonthUtils';
import { onboardingEmployeeUpdate, getPersonalInformationEmployee } from '../../services/onBoardingEmployeesService';

const EmployeeJoiningForm = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const employeeUniqueId = searchParams.get('employeeUniqueId') || location.state?.applicationId;
  const [personalData, setPersonalData] = useState({
    id: '',
    surName: '',
    employeeName: '',
    contactNumber: '',
    designationName: '',
    reportDate: new Date().toISOString().split('T')[0],
    presentAddress: '',
    permanentAddress: '',
  });
  const [errors, setErrors] = useState({
    surName: '',
    employeeName: '',
    contactNumber: '',
    designationName: '',
    reportDate: '',
    presentAddress: '',
    permanentAddress: '',
  });
  const [isFocused, setIsFocused] = useState({
    reportDate: false,
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const dateInputRef = useRef(null);

  // Fetch personal info based on employeeUniqueId
  useEffect(() => {
    const fetchPersonalInfo = async () => {
      try {
        // const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/documents-verification/${employeeUniqueId}`);
        // const data = response.data;
        const data = await getPersonalInformationEmployee(employeeUniqueId);
        const personalInfo = data.personalInformationDTOList?.[0] || {};
        const address = personalInfo.employeeAddress || {};

        const presentAddress = [
          address.presentAddressLine1,
          address.presentAddressLine2,
          address.presentBlock,
          address.presentDistrict,
          address.presentState,
          address.presentCountry,
          address.presentPostCode,
        ]
          .filter(Boolean)
          .join(', ') || '';

        const permanentAddress = [
          address.permanentAddressLine1,
          address.permanentAddressLine2,
          address.permanentBlock,
          address.permanentDistrict,
          address.permanentState,
          address.permanentCountry,
          address.permanentPostCode,
        ]
          .filter(Boolean)
          .join(', ') || '';

        setPersonalData(prev => ({
          ...prev,
          id: data.id || prev.id,
          surName: personalInfo.surName || prev.surName,
          employeeName: personalInfo.name || prev.employeeName,
          contactNumber: personalInfo.mobileNumber?.toString().slice(0, 10) || prev.contactNumber,
          designationName: data.designation || prev.designationName,
          reportDate: data.joiningDate || prev.reportDate,
          presentAddress,
          permanentAddress,
        }));
        setIsDataLoaded(true);
      } catch (error) {
        console.error('Error fetching personal info:', error);
        setErrors(prev => ({ ...prev, general: 'Error fetching personal information' }));
      }
    };
    if (employeeUniqueId) {
      fetchPersonalInfo();
    }
  }, [employeeUniqueId]);

  // Load localStorage on component mount
  useEffect(() => {
    const savedPersonalData = localStorage.getItem('personalData');
    if (savedPersonalData && !employeeUniqueId) {
      const parsedPersonalData = JSON.parse(savedPersonalData);
      setPersonalData({
        ...personalData,
        ...parsedPersonalData,
      });
    }
    setIsDataLoaded(true);
  }, []);

  // Save personalData to localStorage on every change
  useEffect(() => {
    if (isDataLoaded) {
      const personalDataToSave = { ...personalData, photoFile: null };
      localStorage.setItem('personalData', JSON.stringify(personalDataToSave));
    }
  }, [personalData, isDataLoaded]);

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    if (!personalData.employeeName) newErrors.employeeName = 'Full Name is required';
    if (!personalData.surName) newErrors.surName = 'Surname is required';
    if (!personalData.contactNumber || !/^[0-9]{10}$/.test(personalData.contactNumber))
      newErrors.contactNumber = 'Valid 10-digit Contact Number is required';
    if (!personalData.designationName) newErrors.designationName = 'Designation is required';
    if (!personalData.reportDate) newErrors.reportDate = 'Report Date is required';
    if (!personalData.presentAddress) newErrors.presentAddress = 'Present Address is required';
    if (!personalData.permanentAddress) newErrors.permanentAddress = 'Permanent Address is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      joiningToday: true,
      status: 'INACTIVE',
      // auditLogRequest: {
      //   name: 'gandhi', 
      //   role: 'Admin',
      //   employeeId: '4GID101',
      // },
      auditLogRequest: getAuditLog(),
    };

    try {
      // await axios.patch(`${process.env.REACT_APP_API_BASE_URL}/api/documents-verification/${employeeUniqueId}/update-status`,payload,
      //   {
      //     headers: {
      //       'Content-Type': 'application/json',
      //     },
      //   }
      // );
      await onboardingEmployeeUpdate(employeeUniqueId, payload);
      alert('Joining form submitted successfully!');
      localStorage.removeItem('personalData');
      setPersonalData({
        id: '',
        surName: '',
        employeeName: '',
        contactNumber: '',
        designationName: '',
        reportDate: new Date().toISOString().split('T')[0],
        presentAddress: '',
        permanentAddress: '',
      });
      navigate('/employeeApplications/onBoardingPending');
    } catch (error) {
      console.error('Error submitting Joining form:', error);
      setErrors(prev => ({
        ...prev,
        general: error.response?.data?.message || 'Failed to submit Joining form. Please try again.',
      }));
    }
  };

  const handleExit = () => {
    navigate('/employeeApplications/onBoardingPending');
  };

  const currentYear = new Date().getFullYear();
  const minDate = new Date(1965, 0, 1).toISOString().split('T')[0];
  const today = new Date();
  const maxDate = today.toISOString().split('T')[0];

  return (
    <React.Fragment>
      <div className="joining-container">
        <Routes>
          <Route path="/registrationnavbar" element={<RegistrationNavbar />} />
        </Routes>
        <div className="joining-box">
          <h1 className="joining-heading">Employee Joining Form</h1>
          {errors.general && <div className="error-message">{errors.general}</div>}
          <div className="pendingdetails-heading">
            <span className="highlight-label">Temporary ID :</span>{' '}
            {employeeUniqueId}-{personalData.employeeName} {personalData.surName}-{personalData.designationName}
          </div>
          <form onSubmit={handleSubmit}>
            <div className="joining-maincontainer">
              <div className="joining-container-part">
                <div className="joining-floating-label-container-1">
                  <input
                    type="text"
                    name="employeeName"
                    value={`${personalData.employeeName || ''} ${personalData.surName || ''}`.trim()}
                    placeholder=" "
                    disabled
                    required
                  />
                  <label>
                    <span className="required">*</span>Full Name
                  </label>
                  {errors.employeeName && <div className="error-message">{errors.employeeName}</div>}
                </div>
              </div>
              <div className="joining-container-part">
                <div className="joining-floating-label-container-1">
                  <input
                    type="text"
                    name="designationName"
                    value={personalData.designationName || ''}
                    disabled
                    required
                    placeholder=" "
                  />
                  <label>
                    <span className="required">*</span>Designation
                  </label>
                  {errors.designationName && <div className="error-message">{errors.designationName}</div>}
                </div>
              </div>
            </div>
            <div className="joining-maincontainer">
              <div className="joining-container-part">
                <div className="joining-floating-label-container-1">
                  <input
                    type="text"
                    name="contactNumber"
                    value={personalData.contactNumber}
                    disabled
                    required
                    placeholder=" "
                    maxLength="10"
                    pattern="^[0-9]{10}$"
                    title="Please enter a valid 10-digit phone number."
                  />
                  <label>
                    <span className="required">*</span>Contact Number
                  </label>
                  {errors.contactNumber && <div className="error-message">{errors.contactNumber}</div>}
                </div>
              </div>
              <div className="joining-container-part">
                <div className={`joining-floating-label-container-2 ${isFocused.reportDate || !!personalData.reportDate ? 'float-label' : ''}`}>
                  <DatePicker
                    selected={personalData.reportDate ? new Date(personalData.reportDate) : null}
                    dateFormat="dd-MM-yyyy"
                    placeholderText=""
                    className="did-floating-input"
                    onFocus={() => setIsFocused(prev => ({ ...prev, reportDate: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, reportDate: false }))}
                    ref={dateInputRef}
                    minDate={new Date(minDate)}
                    maxDate={new Date(maxDate)}
                    disabled
                  />
                  <label>
                    <span className="required">*</span>Report Date
                  </label>
                  {errors.reportDate && <div className="error-message">{errors.reportDate}</div>}
                </div>
              </div>
            </div>
            <div className="address-container">
              <div className="address-section">
                <div className="joining-maincontainer">
                  <div className="joining-container-part">
                    <div className="joining-floating-label-container-1">
                      <input
                        type="text"
                        name="presentAddress"
                        value={personalData.presentAddress}
                        disabled
                        required
                        placeholder=" "
                      />
                      <label>
                        <span className="required">*</span>Present Address
                      </label>
                      {errors.presentAddress && <div className="error-message">{errors.presentAddress}</div>}
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="address-section">
                <div className="joining-maincontainer">
                  <div className="joining-container-part">
                    <div className="joining-floating-label-container-1">
                      <input
                        type="text"
                        name="permanentAddress"
                        value={personalData.permanentAddress}
                        disabled
                        required
                        placeholder=" "
                      />
                      <label>
                        <span className="required">*</span>Permanent Address
                      </label>
                      {errors.permanentAddress && <div className="error-message">{errors.permanentAddress}</div>}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
            <div className="joining-btn-div">
              <button type="submit" className="joining-btn">
                Confirm
              </button>
              <button type="button" className="joining-btn-can" onClick={handleExit}>
                Exit
              </button>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default EmployeeJoiningForm;